import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  warning: 'bg-amber-50  text-amber-700  dark:bg-amber-900/30  dark:text-amber-400',
  danger:  'bg-red-50    text-red-700    dark:bg-red-900/30    dark:text-red-400',
  info:    'bg-blue-50   text-blue-700   dark:bg-blue-900/30   dark:text-blue-400',
  purple:  'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-gray-400',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger:  'bg-red-500',
  info:    'bg-blue-500',
  purple:  'bg-purple-500',
};

export const Badge = ({ children, variant = 'default', size = 'sm', dot = false, className }: BadgeProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 font-medium rounded-full',
      size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
      variantClasses[variant],
      className
    )}
  >
    {dot && (
      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dotColors[variant])} />
    )}
    {children}
  </span>
);
